import React from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS } from '../constants/colors'

export interface NumberTrackerProps {
  /** Array of items to track */
  items: Array<{ id: string }>
  /** Current active item index */
  activeIndex?: number
  /** Whether to show the tracker */
  visible?: boolean
  /** Additional CSS classes */
  className?: string
  /** Custom spacing between items */
  itemSpacing?: number
  /** Height of each item */
  itemHeight?: number
  /** Optional measured heights for each item */
  itemHeights?: number[]
}

/**
 * NumberTracker Component
 *
 * A vertical dotted line with numbers that tracks progress through a list of items.
 * The dotted line extends above the first item, between items, and below the last item.
 * Numbers are positioned at the center of each item.
 *
 * @param props - Props for the NumberTracker component
 */
const NumberTracker: React.FC<NumberTrackerProps> = ({
  items,
  activeIndex = -1,
  visible = true,
  className = '',
  itemSpacing = 16, // Default spacing between items
  itemHeight = 80, // Default height of each item
  itemHeights
}) => {
  if (!visible || items.length === 0) return null

  const resolvedHeights = itemHeights && itemHeights.length === items.length ? itemHeights : items.map(() => itemHeight)

  const totalHeight = resolvedHeights.reduce((sum, height) => sum + height, 0) + (items.length - 1) * itemSpacing
  const dotSpacing = 8
  const dotMaskRadius = 14
  const numberPositions = items.map((_, index) => {
    const offsetBefore = resolvedHeights.slice(0, index).reduce((sum, height) => sum + height, 0) + index * itemSpacing
    return offsetBefore + resolvedHeights[index] / 2
  })

  const dotPositions: number[] = []

  for (let position = 0; position <= totalHeight; position += dotSpacing) {
    const shouldSkipDot = numberPositions.some((numberPosition) => Math.abs(position - numberPosition) < dotMaskRadius)

    if (!shouldSkipDot) {
      dotPositions.push(position)
    }
  }

  return (
    <Box
      className={`relative flex flex-col items-center ${className}`}
      sx={{
        width: '48px', // Fixed width for the tracker
        height: `${totalHeight}px`
      }}
    >
      {dotPositions.map((position) => (
        <Box
          key={position}
          sx={{
            position: 'absolute',
            top: `${position}px`,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '2px',
            height: '2px',
            borderRadius: '50%',
            backgroundColor: INTERAX_COLORS.thunderGrey,
            opacity: 1,
            zIndex: 1
          }}
        />
      ))}

      {/* Numbers */}
      {items.map((item, index) => {
        const isActive = index === activeIndex
        const topPosition = numberPositions[index]

        return (
          <Box
            key={item.id}
            sx={{
              position: 'absolute',
              top: `${topPosition}px`,
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <Box
              sx={{
                color: isActive ? INTERAX_COLORS.sun : INTERAX_COLORS.white,
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Lexend Deca, system-ui, sans-serif'
              }}
            >
              {index + 1}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default NumberTracker
